// cache-bust: v1.3
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import axios, { endpoints } from 'src/utils/axios';
import { useSnackbar } from 'src/components/snackbar';
import { useSiteSettings } from 'src/hooks/use-site-settings';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
// date picker
import { DatePicker, TimePicker, DateTimePicker, MobileDateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// components
import Iconify from 'src/components/iconify';
import {
    RHFTextField,
    RHFSelect,
    RHFAutocomplete,
    RHFAutocompleteAsync,
    RHFSwitch,
    RHFMultiCheckbox,
    RHFRadioGroup,
    RHFSlider,
    RHFIconSelector,
    RHFUpload,
    RHFPdfUpload,
    RHFArrayField,
    RHFEditor,
} from 'src/components/hook-form';
import RHFAutocompleteWithQuickAdd from 'src/components/hook-form/rhf-autocomplete-with-quick-add';
import { blockConfig } from 'src/sections/block-master/blockConfig';
import PlanStructureTree from 'src/sections/plan-master/components/PlanStructureTree';
import FormCompOffDateSelector from './FormCompOffDateSelector';
import FormSignature from './FormSignature'; // Assuming FormSignature is a local component
import FormCalculation from './FormCalculation';
import FormReporteeReassignmentSelector from './FormReporteeReassignmentSelector';

// ── MultiDatePicker — standalone component so hooks are used correctly ──────
// Disables holiday dates (fetched from API) and weekly-off days (from site settings)
function MultiDatePicker({ name, label, isDisabled, fieldProps, siteSettings }) {
    const { watch, setValue, formState: { errors } } = useFormContext();
    const [holidayDates, setHolidayDates] = useState([]);
    const offDays = siteSettings?.offDays || 'sunday';

    useEffect(() => {
        axios.post('/api/admin/holidays/list', { limit: 0, status: 'active' })
            .then((r) => {
                const list = r.data?.data || r.data || [];
                setHolidayDates(list.map((h) => new Date(h.date).toDateString()));
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isWeeklyOff = (date) => {
        const dow = date.getDay();
        if (offDays === 'sunday' && dow === 0) return true;
        if (offDays === 'saturday_sunday_all' && (dow === 0 || dow === 6)) return true;
        if (offDays === '1st_3rd_saturday_sunday') {
            if (dow === 0) return true;
            if (dow === 6) { const wn = Math.ceil(date.getDate() / 7); return wn === 1 || wn === 3; }
        }
        if (offDays === '2nd_4th_saturday_sunday') {
            if (dow === 0) return true;
            if (dow === 6) { const wn = Math.ceil(date.getDate() / 7); return wn === 2 || wn === 4; }
        }
        return false;
    };

    const isHoliday = (date) => holidayDates.includes(date.toDateString());
    const shouldDisableDate = (date) => isHoliday(date) || isWeeklyOff(date);

    return (
        <Box sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label={label}
                    value={null}
                    disabled={isDisabled}
                    shouldDisableDate={shouldDisableDate}
                    onChange={(date) => {
                        if (date) {
                            const currentDates = watch(name) || [];
                            const dateStr = date.toISOString().split('T')[0];
                            const exists = currentDates.some(
                                (d) => new Date(d).toISOString().split('T')[0] === dateStr
                            );
                            if (!exists) {
                                setValue(name, [...currentDates, date]);
                            }
                        }
                    }}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            helperText: 'Click dates to add. Holidays & weekly-off days are disabled.',
                            inputProps: { readOnly: true },
                            ...fieldProps,
                        },
                        day: {
                            sx: {
                                '&.Mui-disabled': {
                                    color: 'error.light',
                                    textDecoration: 'line-through',
                                    opacity: 0.5,
                                },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                {(watch(name) || []).map((date, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 'primary.lighter',
                            color: 'primary.darker',
                            fontSize: '0.875rem',
                        }}
                    >
                        {new Date(date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                        <Box
                            component="span"
                            onClick={() => {
                                const currentDates = watch(name) || [];
                                setValue(name, currentDates.filter((_, i) => i !== index));
                            }}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { opacity: 0.7 } }}
                        >
                            <Iconify icon="mingcute:close-line" width={16} />
                        </Box>
                    </Box>
                ))}
            </Stack>
            {(watch(name) || []).length > 0 && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Total: {(watch(name) || []).length} day(s) selected
                </Typography>
            )}
            {/* Legend */}
            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.light', display: 'inline-block' }} />
                    Holiday / Weekly Off (disabled)
                </Typography>
            </Stack>
            {errors[name] && (
                <Typography variant="caption" sx={{ mt: 0.5, color: 'error.main', display: 'block' }}>
                    {errors[name].message}
                </Typography>
            )}
        </Box>
    );
}
MultiDatePicker.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.node,
    isDisabled: PropTypes.bool,
    fieldProps: PropTypes.object,
    siteSettings: PropTypes.object,
};

// ----------------------------------------------------------------------

export default function RenderFormField({ 
    field, 
    allFields = [], 
    EntityCreationDialog, 
    isPublic, 
    publicOrgId, 
    publicFormId,
    formType,
    autoFillLookupFieldId,
    primaryFormId,
    isVerificationRequired = true
}) {
    const { register, watch, setValue, getValues, formState: { errors } } = useFormContext();
    const { enqueueSnackbar } = useSnackbar();
    const { settings: siteSettings } = useSiteSettings();

    const [verifying, setVerifying] = useState(false);

    const {
        type,
        name,
        label,
        secondaryLabel,
        fieldNote,
        options: initialOptions,
        dependency,
        hidden,
        disabled,
        accept, // Extract accept here so it won't overwrite dropzoneAccept via fieldProps
        required,
        validation,
        ...fieldProps
    } = field;

    // Get current user role from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = user.role || 'admin';

    // Collect ALL recursive fields we need to watch for both conditional logic AND visibility rules
    const conditionalField = field.conditional?.field;
    const dependencyField = dependency?.watch;
    const visibilityRulesField = field.visibilityRules?.field;
    const visibilityGroupRules = field.visibilityGroup?.rules || [];
    
    // Collect all fields we need to subscribe to
    const fieldsToWatch = [name];
    if (conditionalField) fieldsToWatch.push(conditionalField);
    if (dependencyField) fieldsToWatch.push(dependencyField);
    if (visibilityRulesField) fieldsToWatch.push(visibilityRulesField);
    visibilityGroupRules.forEach(rule => {
        if (rule.field) fieldsToWatch.push(rule.field);
    });

    // Subscribes this specific component instance to changes in its dependencies
    const watchedValues = watch(fieldsToWatch);
    
    // Construct reactive formData by merging getValues() with live watchedValues
    const formData = { ...getValues() };
    fieldsToWatch.forEach((fName, idx) => {
      if (fName && watchedValues && watchedValues[idx] !== undefined) {
        formData[fName] = watchedValues[idx];
      }
    });

    // Helper: Primitive Value extraction
    const getPrimitiveValue = (val) => {
        if (val === null || val === undefined) return val;
        if (Array.isArray(val)) {
            return val.length > 0 ? getPrimitiveValue(val[0]) : null;
        }
        if (typeof val === 'object') {
            const innerVal = val.value !== undefined ? val.value : (val.id ?? val._id ?? val);
            if (typeof innerVal === 'object' && innerVal !== val) return getPrimitiveValue(innerVal);
            return innerVal;
        }
        return val;
    };

    // Helper: Rule evaluation
    const evaluateRule = (rule, watchedVal) => {
        const { operator, value: compareVal } = rule;
        const actualValue = getPrimitiveValue(watchedVal);
        const actualCompareVal = getPrimitiveValue(compareVal);
        const normalize = (v) => (typeof v === 'string' ? v.trim() : v);
        const nv = normalize(actualValue);
        const ncv = normalize(actualCompareVal);

        // Special handling for array values (e.g., checkbox-group, multi-select)
        if (Array.isArray(watchedVal)) {
            const unwrapped = watchedVal.map(getPrimitiveValue);
            switch (operator) {
                case 'equals':
                    return unwrapped.some(item => {
                        const nItem = normalize(item);
                        const nComp = normalize(actualCompareVal);
                        if (typeof nItem === 'string' && typeof nComp === 'string') {
                            return nItem.toLowerCase() === nComp.toLowerCase();
                        }
                        // eslint-disable-next-line eqeqeq
                        return nItem == nComp;
                    });
                case 'not_equals':
                    return !unwrapped.some(item => {
                        const nItem = normalize(item);
                        const nComp = normalize(actualCompareVal);
                        if (typeof nItem === 'string' && typeof nComp === 'string') {
                            return nItem.toLowerCase() === nComp.toLowerCase();
                        }
                        // eslint-disable-next-line eqeqeq
                        return nItem == nComp;
                    });
                case 'contains':
                    return unwrapped.some(item =>
                        String(item || '').toLowerCase().includes(String(actualCompareVal || '').toLowerCase())
                    );
                case 'is_filled':
                case 'is_not_empty':
                    return unwrapped.length > 0;
                case 'is_empty':
                    return unwrapped.length === 0;
                default:
                    break;
            }
        }

        switch (operator) {
            case 'equals':
                if (typeof nv === 'string' && typeof ncv === 'string') return nv.toLowerCase() === ncv.toLowerCase();
                // eslint-disable-next-line eqeqeq
                return nv == ncv;
            case 'not_equals':
                if (typeof nv === 'string' && typeof ncv === 'string') return nv.toLowerCase() !== ncv.toLowerCase();
                // eslint-disable-next-line eqeqeq
                return nv != ncv;
            case 'contains':
                return String(nv || '').toLowerCase().includes(String(ncv || '').toLowerCase());
            case 'greater_than':
                return Number(nv) > Number(ncv);
            case 'less_than':
                return Number(nv) < Number(ncv);
            case 'is_filled':
            case 'is_not_empty':
                return actualValue !== null && actualValue !== '' && actualValue !== undefined && (Array.isArray(watchedVal) ? watchedVal.length > 0 : true);
            case 'is_empty':
                return actualValue === null || actualValue === '' || actualValue === undefined || (Array.isArray(watchedVal) && watchedVal.length === 0);
            default:
                return true;
        }
    };

    // Helper: Dynamic Visibility Check
    const isVisibleByRules = () => {
        // 1. Advanced Visibility Group
        if (visibilityGroupRules.length > 0) {
            const match = String(field.visibilityGroup.match || 'AND').toUpperCase();
            const results = visibilityGroupRules.map(rule => evaluateRule(rule, getValues(rule.field)));
            return match === 'OR' ? results.some(r => r) : results.every(r => r);
        }
        // 2. Simple Visibility Rules
        if (field.visibilityRules?.field) {
            return evaluateRule(field.visibilityRules, getValues(field.visibilityRules.field));
        }
        return true;
    };

    // Get current values from the watched array for immediate reactivity
    const watchedValue = watchedValues[0];
    const watchValueCond = conditionalField ? watchedValues[fieldsToWatch.indexOf(conditionalField)] : null;
    const watchedValueDep = dependencyField ? watchedValues[fieldsToWatch.indexOf(dependencyField)] : null;

    // Get options (can be static array or function)
    let options = initialOptions;
    if (typeof initialOptions === 'function') {
        options = initialOptions(formData, userRole);
    }
    
    // Normalize options to ensure they are objects with label and value
    if (Array.isArray(options)) {
        options = options.map(opt => {
            if (typeof opt === 'string' || typeof opt === 'number') {
                return { label: String(opt), value: String(opt) };
            }
            return opt;
        });
    }

    // Handle dependency
    if (dependency && dependency.getOptions) {
        options = dependency.getOptions(watchedValueDep, options || []);
    }

    const isLookupField = formType === 'autoFill' && name === autoFillLookupFieldId;
    const isMappedField = formType === 'autoFill' && !!field.autoFillMappedFieldId;

    // Auto-clear "Other" text field when "Other" option is deselected
    useEffect(() => {
        if (!options || !Array.isArray(options)) return;

        const hasOtherOption = options.some(o => o.isOther);
        if (!hasOtherOption) return;

        let isOtherCurrentlySelected = false;
        if (Array.isArray(watchedValue)) {
            isOtherCurrentlySelected = options.some(o => o.isOther && watchedValue.includes(o.value));
        } else {
            isOtherCurrentlySelected = !!options.find(o => o.value === watchedValue)?.isOther;
        }

        if (!isOtherCurrentlySelected) {
            // Use getValues (NOT watch) inside useEffect
            const otherFieldName = `${name}_other`;
            if (getValues(otherFieldName)) {
                setValue(otherFieldName, '');
            }
        }
    }, [watchedValue, name, options, setValue, getValues]);

    // Auto-reset select value if current value is no longer present in available options
    useEffect(() => {
        if (type === 'select' && options && Array.isArray(options) && options.length > 0 && watchedValue) {
            const isValidValue = options.some(o => o.value === watchedValue);
            if (!isValidValue) {
                setValue(name, options[0].value, { shouldValidate: true });
            }
        }
    }, [watchedValue, name, options, type, setValue]);

    // Reset verification status if lookup field changes
    useEffect(() => {
        if (isLookupField) {
            setValue('_isAutoFillVerified', false);
        }
    }, [watchedValue, isLookupField, setValue]);

    // Programmatically register hidden fields to ensure react-hook-form handles them correctly
    // during reset (e.g. on edit) and form submission
    useEffect(() => {
        if (type === 'hidden') {
            register(name);
        }
    }, [register, name, type]);

    // ─── FINAL VISIBILITY DETERMINATION ───
    
    // Check if field should be hidden (function or group rules)
    const isVisibleFunction = !(typeof hidden === 'function' && hidden(formData, userRole, siteSettings));
    const isVisibleRules = isVisibleByRules();

    // Check legacy conditional rendering
    let isVisibleLegacy = true;
    if (field.conditional) {
        const { condition } = field.conditional;
        if (typeof condition === 'function') {
            isVisibleLegacy = !!condition(watchValueCond);
        }
    }

    const isVisible = isVisibleFunction && isVisibleRules && isVisibleLegacy;

    // Track previous visibility to only clear when it explicitly changes from true to false
    const prevIsVisible = useRef(isVisible);

    // Auto-clear value when field becomes hidden by user action
    useEffect(() => {
        if (prevIsVisible.current === true && isVisible === false) {
            const currentVal = getValues(name);
            const hasValue = Array.isArray(currentVal) 
                ? currentVal.length > 0 
                : (currentVal !== undefined && currentVal !== null && currentVal !== '');
                
            if (hasValue) {
                // Clear the main value
                setValue(name, Array.isArray(currentVal) ? [] : null, { shouldValidate: true, shouldDirty: true });
                
                // Clear associated 'other' text field if it exists
                const otherFieldName = `${name}_other`;
                if (getValues(otherFieldName)) {
                    setValue(otherFieldName, '');
                }
            }
        }
        prevIsVisible.current = isVisible;
    }, [isVisible, name, setValue, getValues]);

    if (!isVisible) return null;

    // Check if field should be disabled
    let isDisabled = false;
    if (isMappedField && !isLookupField && Boolean(isVerificationRequired)) {
        isDisabled = true; // Strict Auto-Fill lockdown for mapped fields
    } else if (typeof disabled === 'function') {
        isDisabled = disabled(formData, userRole, siteSettings);
    } else if (typeof disabled === 'boolean') {
        isDisabled = disabled;
    }

    // Helper: Safe Text Rendering to avoid "Object as child" errors
    const getSafeText = (val) => {
        if (!val) return null;
        
        // If it's a function, evaluate it with the standard arguments
        if (typeof val === 'function') {
            try {
                return getSafeText(val(formData, userRole, siteSettings));
            } catch (e) {
                console.error('Error evaluating function text:', e);
                return '';
            }
        }

        if (typeof val === 'string' || typeof val === 'number') return val;
        if (React.isValidElement(val)) return val;
        if (typeof val === 'object') {
            // Attempt to find a standard name/label field, otherwise stringify
            const labelKey = val.label || val.name || val.title || val.block_name || val.country_name || val.state_name || val.city_name;
            if (labelKey && typeof labelKey === 'string') return labelKey;
            try {
                return JSON.stringify(val);
            } catch (e) {
                return '[Object]';
            }
        }
        return String(val);
    };

    const getRenderedLabel = (l, sl, fn, isRequired) => {
        const safeL = getSafeText(l);
        const safeSl = getSafeText(sl);
        const safeFn = getSafeText(fn);

        if (!safeL) return null;

        return (
            <Stack direction="row" spacing={0.5} alignItems="center" component="span" sx={{ display: 'inline-flex' }}>
                <Typography variant="subtitle2" component="span">
                    {safeL}
                    {isRequired && (
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                            *
                        </Box>
                    )}
                </Typography>
                {safeSl && (
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 'normal', ml: 0.5 }}>
                        {safeSl}
                    </Typography>
                )}
                {safeFn && (
                    <Tooltip title={safeFn} placement="top" arrow>
                        <Box component="span" sx={{ display: 'inline-flex', ml: 0.5, cursor: 'help' }}>
                            <Iconify icon="eva:question-mark-circle-fill" width={16} sx={{ color: 'text.disabled' }} />
                        </Box>
                    </Tooltip>
                )}
            </Stack>
        );
    };

    const isFieldRequired = !!(required || (validation?._exclusive?.required));
    const renderedLabel = getRenderedLabel(label, secondaryLabel, fieldNote, isFieldRequired);

    const handleVerify = async () => {
        const lookupValue = getValues(name);
        if (!lookupValue) {
            enqueueSnackbar('Please enter a value to verify.', { variant: 'warning' });
            return;
        }

        setVerifying(true);
        try {
            const response = await axios.post(endpoints.forms.verifyAndFetch, {
                primaryFormId,
                lookupFieldId: autoFillLookupFieldId,
                lookupValue,
                targetFormId: field._formId 
            });

            if (response.data?.success) {
                const fetchedData = response.data.data;
                const sourceId = response.data.sourceId;

                // 1. Populate mapped fields
                allFields.forEach(f => {
                    if (f.autoFillMappedFieldId && fetchedData[f.autoFillMappedFieldId] !== undefined) {
                        setValue(f.name, fetchedData[f.autoFillMappedFieldId], { shouldValidate: true, shouldDirty: true });
                    }
                });

                // 2. Store the source ID of the auto-fill record
                setValue('autoFillSource', sourceId);

                // 3. Mark as verified
                setValue('_isAutoFillVerified', true);

                enqueueSnackbar('Data verified and auto-populated!', { variant: 'success' });
            }
        } catch (error) {
            console.error('Verify error:', error);
            setValue('_isAutoFillVerified', false);
            enqueueSnackbar(error.response?.data?.message || 'Verification failed.', { variant: 'error' });
        } finally {
            setVerifying(false);
        }
    };


    switch (type) {
        case 'text':
        case 'email':
        case 'password':
        case 'number':
            return (
                <RHFTextField
                    name={name}
                    label={renderedLabel}
                    type={type}
                    disabled={isDisabled}
                    helperText={getSafeText(field.helperText)}
                    onKeyDown={(e) => {
                        if (isLookupField && e.key === 'Enter') {
                            e.preventDefault();
                            handleVerify();
                        }
                    }}
                    InputProps={{
                        endAdornment: isLookupField ? (
                            <InputAdornment position="end">
                                <LoadingButton
                                    size="small"
                                    variant="contained"
                                    onClick={handleVerify}
                                    loading={verifying}
                                    icon={<Iconify icon="solar:check-read-bold" />}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Verify
                                </LoadingButton>
                            </InputAdornment>
                        ) : null
                    }}
                    {...fieldProps}
                />
            );

        case 'mobile':
            return (
                <RHFTextField
                    name={name}
                    label={renderedLabel}
                    type="tel"
                    inputMode="tel"
                    disabled={isDisabled}
                    helperText={getSafeText(field.helperText)}
                    onKeyDown={(e) => {
                        if (isLookupField && e.key === 'Enter') {
                            e.preventDefault();
                            handleVerify();
                        }
                    }}
                    InputProps={{
                        endAdornment: isLookupField ? (
                            <InputAdornment position="end">
                                <LoadingButton
                                    size="small"
                                    variant="contained"
                                    onClick={handleVerify}
                                    loading={verifying}
                                    icon={<Iconify icon="solar:check-read-bold" />}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Verify
                                </LoadingButton>
                            </InputAdornment>
                        ) : null
                    }}
                    {...fieldProps}
                />
            );

        case 'signature':
            return (
                <FormSignature
                    name={field.name}
                    label={renderedLabel}
                    required={required}
                    helperText={field.helperText}
                />
            );

        case 'calculation':
            return (
                <FormCalculation
                    name={field.name}
                    label={renderedLabel}
                    formula={field.formula}
                    helperText={field.helperText}
                    allFields={allFields}
                />
            );

        case 'grid':
            return (
                <RHFArrayField
                    name={field.name}
                    label={renderedLabel}
                    fields={field.fields || []}
                    minItems={field.minItems}
                    maxItems={field.maxItems}
                    EntityCreationDialog={EntityCreationDialog}
                />
            );

        case 'select': {
            // ── System data source (geography / project_members) ──────────────
            if (field.dataSource === 'system' && field.systemMaster) {
                // Geography config: correct label field + optional parent filter param
                const GEO = {
                    country: { base: isPublic ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=country` : '/api/admin/countries/list?limit=0', label: 'country_name', parentParam: null },
                    state: { base: isPublic ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=state` : '/api/admin/states/list?limit=0', label: 'state_name', parentParam: 'country' },
                    city: { base: isPublic ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=city` : '/api/admin/cities/list?limit=0', label: 'city_name', parentParam: 'state' },
                    block: { base: isPublic ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=block` : '/api/admin/blocks/list?limit=0', label: 'block_name', parentParam: 'city' },
                };
                const geo = GEO[field.systemMaster];

                let apiEndpoint;
                const optLabel = field.optionLabel || (geo ? geo.label : 'name');

                if (geo) {
                    let baseWithAll = geo.base;
                    if (field.fetchFullGeography) {
                        baseWithAll += '&fetchAll=true';
                    }

                    if (field.dependsOn && geo.parentParam) {
                        // Cascading: build a function so RHFAutocompleteAsync re-fetches on parent change
                        apiEndpoint = (formVals) => {
                            const pv = formVals?.[field.dependsOn];
                            const pid = pv?._id || pv?.id || (typeof pv === 'string' ? pv : null);
                            return pid ? `${baseWithAll}&${geo.parentParam}=${pid}` : null;
                        };
                    } else {
                        apiEndpoint = baseWithAll;
                    }
                } else if (field.systemMaster === 'project_members') {
                    // projectId is stored on the field by normalizeBuilderField or by FormDataListPage
                    if (isPublic) {
                        apiEndpoint = field._projectId ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=project_members&projectId=${field._projectId}` : null;
                    } else {
                        apiEndpoint = field._projectId
                            ? `/api/admin/project-assignments/project/${field._projectId}/members`
                            : field.apiEndpoint || null;
                    }
                } else {
                    apiEndpoint = isPublic 
                        ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=${field.systemMaster}`
                        : (field.apiEndpoint || `/api/admin/${field.systemMaster}/list`);
                }

                // For block with Quick Add — let field workers add new blocks inline
                if (field.systemMaster === 'block' && field.allowQuickAdd) {
                    const blockQuickAddConfig = {
                        buttonLabel: 'Add New Block',
                        buttonIcon: 'mingcute:add-line',
                        formConfig: blockConfig,  // uses same pattern as adminUserConfig → organizationConfig
                    };
                    return (
                        <RHFAutocompleteWithQuickAdd
                            name={name}
                            label={renderedLabel}
                            apiEndpoint={apiEndpoint}
                            optionLabel={optLabel}
                            optionValue={field.optionValue || '_id'}
                            placeholder={field.placeholder || `Select ${label}`}
                            multiple={!!field.multiple}
                            disabled={isDisabled}
                            quickAddConfig={blockQuickAddConfig}
                            EntityCreationDialog={EntityCreationDialog}
                            {...fieldProps}
                        />
                    );
                }

                return (
                    <RHFAutocompleteAsync
                        name={name}
                        label={renderedLabel}
                        apiEndpoint={apiEndpoint}
                        optionLabel={optLabel}
                        optionValue={field.optionValue || '_id'}
                        mapToId={field.mapToId !== undefined ? field.mapToId : true}
                        placeholder={getSafeText(field.placeholder || `Select ${label}`)}
                        helperText={getSafeText(field.helperText)}
                        multiple={!!field.multiple}
                        disabled={isDisabled}
                        {...fieldProps}
                    />
                );
            }

            // ── Custom form data source ───────────────────────────────────────
            if (field.dataSource === 'custom_form' && field.systemMaster) {
                const labelKey = field.optionLabel || 'name';
                const valueKey = field.optionValue || '_id';
                const base = isPublic
                    ? `/api/public/forms/${publicOrgId}/${publicFormId}/options?type=custom_form&formId=${field.systemMaster}&fields=${labelKey},${valueKey}`
                    : `/api/admin/form-data?formId=${field.systemMaster}&fields=${labelKey},${valueKey}&forDropdown=true`;
                const apiEndpoint = field.dependsOn
                    ? (formVals) => {
                        const pv = formVals?.[field.dependsOn];
                        const pid = pv?._id || pv?.id || (typeof pv === 'string' ? pv : null);
                        return pid ? `${base}&filterField=${field.dependsOn}&filterValue=${pid}` : base;
                    }
                    : base;

                if (field.allowQuickAdd && field.quickAddConfig) {
                    return (
                        <RHFAutocompleteWithQuickAdd
                            name={name}
                            label={renderedLabel}
                            apiEndpoint={apiEndpoint}
                            optionLabel={labelKey}
                            optionValue={valueKey}
                            placeholder={field.placeholder || `Select ${label}`}
                            multiple={!!field.multiple}
                            disabled={isDisabled}
                            quickAddConfig={field.quickAddConfig}
                            EntityCreationDialog={EntityCreationDialog}
                            {...fieldProps}
                        />
                    );
                }

                return (
                    <RHFAutocompleteAsync
                        name={name}
                        label={renderedLabel}
                        apiEndpoint={apiEndpoint}
                        optionLabel={labelKey}
                        optionValue={valueKey}
                        mapToId={field.mapToId !== undefined ? field.mapToId : true}
                        placeholder={field.placeholder || `Select ${label}`}
                        multiple={!!field.multiple}
                        disabled={isDisabled}
                        {...fieldProps}
                    />
                );
            }

            // ── All static options use Autocomplete for consistent searchable experience ──
            return (
                <Box>
                    <RHFAutocomplete
                        name={name}
                        label={renderedLabel}
                        options={(options || []).map(o => ({ label: o.label, value: o.value }))}
                        placeholder={field.placeholder || `Select ${label}`}
                        multiple={!!field.multiple}
                        disabled={isDisabled}
                        {...fieldProps}
                    />
                    {(() => {
                        const isOtherSelected = options?.find(o => o.value === getPrimitiveValue(watch(name)))?.isOther;
                        if (isOtherSelected) {
                            return (
                                <RHFTextField
                                    name={`${name}_other`}
                                    label={getRenderedLabel(
                                        field.otherLabel || 'Please specify',
                                        field.otherSecondaryLabel,
                                        field.otherTooltip
                                    )}
                                    sx={{ mt: 1 }}
                                    size="small"
                                />
                            );
                        }
                        return null;
                    })()}
                </Box>
            );
        }

        case 'autocomplete':
            // Use async autocomplete if apiEndpoint is provided
            if (field.apiEndpoint || isPublic) {
                const endpoint = field.apiEndpoint;
                
                // If it's public and looks like an internal admin API call, try to rewrite it
                let finalEndpoint = endpoint;
                if (isPublic && typeof endpoint === 'string' && endpoint.includes('/api/admin/')) {
                    // Quick fallback: public links can't hit admin APIs. 
                    // To do this perfectly, the option endpoint needs to be a standard string config,
                    // but we don't have enough metadata. We just disable autocomplete fetch here if it tries to hit auth'd endpoint.
                    finalEndpoint = null;
                }

                // Use quick add version if quickAddConfig is provided
                if (field.quickAddConfig && !isPublic) {
                    return (
                        <RHFAutocompleteWithQuickAdd
                            name={name}
                            label={renderedLabel}
                            apiEndpoint={finalEndpoint}
                            optionLabel={field.optionLabel}
                            optionValue={field.optionValue}
                            placeholder={field.placeholder || `Select ${label} `}
                            disabled={isDisabled}
                            quickAddConfig={field.quickAddConfig}
                            EntityCreationDialog={EntityCreationDialog} // Pass injected dialog
                            {...fieldProps}
                        />
                    );
                }

                return (
                    <RHFAutocompleteAsync
                        name={name}
                        label={renderedLabel}
                        apiEndpoint={finalEndpoint}
                        optionLabel={field.optionLabel}
                        optionValue={field.optionValue}
                        mapToId={field.mapToId !== undefined ? field.mapToId : true}
                        placeholder={getSafeText(field.placeholder || `Select ${label} `)}
                        helperText={getSafeText(field.helperText)}
                        disabled={isDisabled}
                        {...fieldProps}
                    />
                );
            }

            // Use regular autocomplete for static options
            return (
                <Box>
                    <RHFAutocomplete
                        name={name}
                        label={renderedLabel}
                        options={options || []}
                        placeholder={field.placeholder || `Select ${label} `}
                        disabled={isDisabled}
                        {...fieldProps}
                    />
                    {(() => {
                        const isOtherSelected = options?.find(o => o.value === getPrimitiveValue(watch(name)))?.isOther;
                        if (isOtherSelected) {
                            return (
                                <RHFTextField
                                    name={`${name}_other`}
                                    label={getRenderedLabel(
                                        field.otherLabel || 'Please specify',
                                        field.otherSecondaryLabel,
                                        field.otherTooltip
                                    )}
                                    sx={{ mt: 1 }}
                                    size="small"
                                />
                            );
                        }
                        return null;
                    })()}
                </Box>
            );

        case 'radio': {
            return (
                <RHFRadioGroup
                    name={name}
                    label={renderedLabel}
                    options={options}
                    row={!!field.row}
                    disabled={isDisabled}
                    helperText={getSafeText(field.helperText)}
                    {...fieldProps}
                />
            );
        }

        case 'textarea':
            return (
                <RHFTextField name={name} label={renderedLabel} multiline rows={4} disabled={isDisabled} {...fieldProps} />
            );

        case 'date': {
            const error = errors[name];
            const dateValue = watch(name);
            // Ensure we only pass valid Date objects or null to DatePicker
            // Handle both Date objects and date strings
            const validDateValue = (() => {
                if (!dateValue) return null;

                // If it's already a Date object and valid
                if (dateValue instanceof Date) {
                    return !Number.isNaN(dateValue.getTime()) ? dateValue : null;
                }

                // If it's a string, try to convert it to Date
                if (typeof dateValue === 'string') {
                    const d = new Date(dateValue);
                    return !Number.isNaN(d.getTime()) ? d : null;
                }

                return null;
            })();

            // Calculate dynamic limits based on constraint and offset
            let calculatedMinDate = validation?.minDate ? new Date(validation.minDate) : undefined;
            let calculatedMaxDate = validation?.maxDate ? new Date(validation.maxDate) : undefined;

            if (validation?.dateConstraint) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const offset = Number(validation.dateOffset || 0);

                if (validation.dateConstraint === 'future') {
                    const constraintMin = new Date(today);
                    constraintMin.setDate(constraintMin.getDate() + offset);
                    
                    if (!calculatedMinDate || constraintMin > calculatedMinDate) {
                        calculatedMinDate = constraintMin;
                    }
                } else if (validation.dateConstraint === 'past') {
                    const constraintMax = new Date(today);
                    constraintMax.setDate(constraintMax.getDate() - offset);
                    constraintMax.setHours(23, 59, 59, 999);

                    if (!calculatedMaxDate || constraintMax < calculatedMaxDate) {
                        calculatedMaxDate = constraintMax;
                    }
                }
            }

            return (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label={renderedLabel}
                        format="dd/MM/yyyy"
                        value={validDateValue}
                        minDate={calculatedMinDate}
                        maxDate={calculatedMaxDate}
                        disabled={isDisabled}
                        onChange={(date) => setValue(name, date, { shouldValidate: true })}
                        slots={{
                            openPickerIcon: () => <Iconify icon="solar:calendar-bold" width={24} />,
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: !!error,
                                helperText: error?.message,
                                ...fieldProps,
                            },
                        }}
                    />
                </LocalizationProvider>
            );
        }

        case 'time': {
            const error = errors[name];
            return (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                        label={renderedLabel}
                        value={watch(name)}
                        disabled={isDisabled}
                        minTime={field.validation?.minTime ? (() => {
                            const [h, m] = field.validation.minTime.split(':').map(Number);
                            const d = new Date();
                            d.setHours(h, m, 0, 0);
                            return d;
                        })() : undefined}
                        maxTime={field.validation?.maxTime ? (() => {
                            const [h, m] = field.validation.maxTime.split(':').map(Number);
                            const d = new Date();
                            d.setHours(h, m, 0, 0);
                            return d;
                        })() : undefined}
                        onChange={(time) => setValue(name, time, { shouldValidate: true })}
                        slots={{
                            openPickerIcon: () => <Iconify icon="solar:clock-circle-bold" width={24} />,
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: !!error,
                                helperText: error?.message,
                                ...fieldProps,
                            },
                        }}
                    />
                </LocalizationProvider>
            );
        }

        case 'datetime':
        case 'datetime-local': {
            const error = errors[name];
            const dateValue = watch(name);
            const validDateValue = (() => {
                if (!dateValue) return null;
                if (dateValue instanceof Date) {
                    return !Number.isNaN(dateValue.getTime()) ? dateValue : null;
                }
                if (typeof dateValue === 'string') {
                    const d = new Date(dateValue);
                    return !Number.isNaN(d.getTime()) ? d : null;
                }
                return null;
            })();

            return (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileDateTimePicker
                        label={renderedLabel}
                        format="dd/MM/yyyy hh:mm a"
                        value={validDateValue}
                        disabled={isDisabled}
                        onChange={(date) => setValue(name, date, { shouldValidate: true })}
                        slots={{
                            openPickerIcon: () => <Iconify icon="solar:calendar-date-bold" width={24} />,
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: !!error,
                                helperText: error?.message,
                                ...fieldProps,
                            },
                        }}
                    />
                </LocalizationProvider>
            );
        }

        case 'multidate':
            return (
                <MultiDatePicker
                    name={name}
                    label={renderedLabel}
                    isDisabled={isDisabled}
                    fieldProps={fieldProps}
                    siteSettings={siteSettings}
                />
            );

        case 'editor':
            return (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: errors[name] ? 'error.main' : 'text.secondary' }}>
                        {renderedLabel}
                    </Typography>
                    <RHFEditor
                        name={name}
                        helperText={getSafeText(field.helperText)}
                        {...fieldProps}
                    />
                </Box>
            );

        case 'switch':
            return <RHFSwitch name={name} label={renderedLabel} disabled={isDisabled} helperText={getSafeText(field.helperText)} {...fieldProps} />;

        case 'slider':
            return (
                <Box sx={{ px: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: errors[name] ? 'error.main' : 'text.secondary' }}>
                        {renderedLabel}
                    </Typography>
                    <RHFSlider
                        name={name}
                        step={field.step || 1}
                        min={field.min || 0}
                        max={field.max || 100}
                        disabled={isDisabled}
                        helperText={getSafeText(field.helperText)}
                        {...fieldProps}
                    />
                </Box>
            );

        case 'rating': {
            const error = errors[name];
            const maxStars = field.max || 5;
            return (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: error ? 'error.main' : 'text.secondary' }}>
                        {renderedLabel} {field.required && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                    <Rating
                        name={name}
                        value={watch(name) || 0}
                        max={maxStars}
                        size="large"
                        disabled={isDisabled}
                        onChange={(event, newValue) => {
                            setValue(name, newValue, { shouldValidate: true });
                        }}
                        sx={{
                            fontSize: '2.5rem',
                            '& .MuiRating-iconFilled': {
                                color: 'warning.main',
                            },
                            '& .MuiRating-iconHover': {
                                color: 'warning.dark',
                            },
                        }}
                    />
                    {error && (
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'error.main', display: 'block' }}>
                            {error.message}
                        </Typography>
                    )}
                    {field.helperText && !error && (
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}>
                            {field.helperText}
                        </Typography>
                    )}
                </Box>
            );
        }

        case 'color': {
            const error = errors[name];
            const colorValue = watch(name) || '#1976d2';
            return (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: error ? 'error.main' : 'text.secondary' }}>
                        {renderedLabel} {field.required && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1,
                                border: '2px solid',
                                borderColor: 'divider',
                                backgroundColor: colorValue,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                    borderColor: isDisabled ? 'divider' : 'primary.main',
                                },
                                opacity: isDisabled ? 0.6 : 1,
                            }}
                        >
                            <input
                                type="color"
                                value={colorValue}
                                disabled={isDisabled}
                                onChange={(e) => setValue(name, e.target.value, { shouldValidate: true })}
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <RHFTextField
                                name={name}
                                placeholder="#000000"
                                size="small"
                                disabled={isDisabled}
                                inputProps={{
                                    style: { textTransform: 'uppercase' }
                                }}
                                helperText={error?.message || field.helperText || 'Click the color box or enter a hex code'}
                            />
                        </Box>
                    </Stack>
                </Box>
            );
        }

        case 'checkbox-group':
            return (
                <Box>
                    <RHFMultiCheckbox
                        name={name}
                        label={renderedLabel}
                        options={options}
                        row={!!field.row}
                        disabled={isDisabled}
                        helperText={getSafeText(field.helperText)}
                        {...fieldProps}
                    />
                    {(() => {
                        const currentValues = watch(name) || [];
                        const isOtherSelected = options?.some(o => o.isOther && currentValues.includes(o.value));
                        if (isOtherSelected) {
                            return (
                                <RHFTextField
                                    name={`${name}_other`}
                                    label={getRenderedLabel(
                                        field.otherLabel || 'Please specify',
                                        field.otherSecondaryLabel,
                                        field.otherTooltip
                                    )}
                                    sx={{ mt: 1 }}
                                    size="small"
                                />
                            );
                        }
                        return null;
                    })()}
                </Box>
            );

        case 'iconSelector':
            return <RHFIconSelector name={name} label={renderedLabel} disabled={isDisabled} {...fieldProps} />;

        case 'plan-tree':
            return <PlanStructureTree />;

        case 'hidden':
            return null;

        case 'file': {
            const isPdfOnly =
                field.accept === '.pdf' || field.accept === 'application/pdf';

            // react-dropzone expects an object like { 'image/*': [], 'application/pdf': ['.pdf'] }
            let dropzoneAccept;
            if (field.accept) {
                dropzoneAccept = {};
                const parts = field.accept.split(',').map(p => p.trim());
                parts.forEach(part => {
                    if (part === 'image/*') {
                        dropzoneAccept['image/*'] = ['.png', '.gif', '.jpeg', '.jpg', '.svg'];
                    } else if (part === 'video/*') {
                        dropzoneAccept['video/*'] = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
                    } else if (part.startsWith('.')) {
                        // fallback for extension, assuming arbitrary category
                        dropzoneAccept['application/octet-stream'] = dropzoneAccept['application/octet-stream'] || [];
                        dropzoneAccept['application/octet-stream'].push(part);
                    } else {
                        dropzoneAccept[part] = []; 
                    }
                });
                
                // Special mapping for common ones from FormBuilder
                if (field.accept === '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                   dropzoneAccept = {
                       'application/msword': ['.doc'],
                       'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
                   };
                }
                if (field.accept === '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                   dropzoneAccept = {
                       'application/vnd.ms-excel': ['.xls'],
                       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
                   };
                }
                if (field.accept === '.pdf') {
                    dropzoneAccept = { 'application/pdf': ['.pdf'] };
                }
                if (field.accept === '.pdf,image/*,.doc,.docx,.xls,.xlsx') {
                    dropzoneAccept = {
                        'application/pdf': ['.pdf'],
                        'image/*': ['.png', '.gif', '.jpeg', '.jpg', '.svg'],
                        'application/msword': ['.doc'],
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                        'application/vnd.ms-excel': ['.xls'],
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
                    }
                }
            }

            if (isPdfOnly) {
                return (
                    <RHFPdfUpload
                        name={name}
                        label={renderedLabel}
                        maxSize={field.maxSize || 5242880}
                        helperText={field.helperText || 'Upload PDF only (Max 5MB)'}
                        accept={dropzoneAccept}
                        disabled={isDisabled}
                        {...fieldProps}
                    />
                );
            }

            return (
                <RHFUpload
                    name={name}
                    label={renderedLabel}
                    maxSize={field.maxSize || 3145728}
                    helperText={field.helperText || 'Max 3MB'}
                    accept={dropzoneAccept}
                    disabled={isDisabled}
                    {...fieldProps}
                />
            );
        }

        case 'array':
            return (
                <RHFArrayField
                    name={name}
                    label={renderedLabel}
                    fields={field.fields || []}
                    minItems={field.minItems}
                    maxItems={field.maxItems}
                    defaultItem={field.defaultItem || {}}
                    disabled={isDisabled}
                    EntityCreationDialog={EntityCreationDialog}
                    {...fieldProps}
                />
            );

        case 'compOffDateSelector':
            return (
                <FormCompOffDateSelector
                    name={name}
                    label={renderedLabel}
                    apiEndpoint={field.apiEndpoint}
                    getOptionLabel={field.getOptionLabel}
                    getOptionValue={field.getOptionValue}
                    disabled={isDisabled}
                    {...fieldProps}
                />
            );

        case 'reporteeReassignmentSelector':
            return (
                <FormReporteeReassignmentSelector
                    name={name}
                    label={renderedLabel}
                    required={field.required}
                    disabled={isDisabled}
                    EntityCreationDialog={EntityCreationDialog}
                    {...fieldProps}
                />
            );

        case 'section_break':
            return (
                <Box sx={{ mt: 2, mb: 1, width: '100%' }}>
                    <Typography variant="h6">{renderedLabel}</Typography>
                    {field.description && (
                        <Typography variant="body2" color="text.secondary">
                            {field.description}
                        </Typography>
                    )}
                </Box>
            );

        case 'component':
            // Custom component rendered by the config — passes formData & setValue
            return field.render ? field.render({ formData, setValue, watch, errors }) : null;

        default:
            return null;
    }
}

RenderFormField.propTypes = {
    field: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        label: PropTypes.string,
        secondaryLabel: PropTypes.string,
        fieldNote: PropTypes.string,
        options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
        dependency: PropTypes.object,
        hidden: PropTypes.func,
        disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
        quickAddConfig: PropTypes.object,
        gridProps: PropTypes.object,
        dependsOn: PropTypes.string,
        computeValue: PropTypes.func,
        apiEndpoint: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        optionLabel: PropTypes.string,
        optionValue: PropTypes.string,
        mapToId: PropTypes.bool,
        placeholder: PropTypes.string,
        conditional: PropTypes.object,
        accept: PropTypes.string,
        maxSize: PropTypes.number,
        helperText: PropTypes.string,
        otherLabel: PropTypes.string,
        otherSecondaryLabel: PropTypes.string,
        otherTooltip: PropTypes.string,
        otherRequired: PropTypes.bool,
        fields: PropTypes.array,
        minItems: PropTypes.number,
        maxItems: PropTypes.number,
        step: PropTypes.number,
        min: PropTypes.number,
        defaultItem: PropTypes.object,
        getOptionLabel: PropTypes.func,
        getOptionValue: PropTypes.func,
        max: PropTypes.number,
        required: PropTypes.bool,
        formula: PropTypes.string,
        description: PropTypes.string,
        // Selection / Dropdown props
        dataSource: PropTypes.string,
        systemMaster: PropTypes.string,
        multiple: PropTypes.bool,
        searchable: PropTypes.bool,
        fetchFullGeography: PropTypes.bool,
        row: PropTypes.bool,
        _projectId: PropTypes.string,
        allowQuickAdd: PropTypes.bool,
        render: PropTypes.func,
        validation: PropTypes.object,
        _formId: PropTypes.string,
        autoFillMappedFieldId: PropTypes.string,
        visibilityRules: PropTypes.shape({
            field: PropTypes.string,
            operator: PropTypes.string,
            value: PropTypes.any,
        }),
        visibilityGroup: PropTypes.shape({
            match: PropTypes.string,
            rules: PropTypes.arrayOf(
                PropTypes.shape({
                    field: PropTypes.string,
                    operator: PropTypes.string,
                    value: PropTypes.any,
                })
            ),
        }),
    }).isRequired,
    EntityCreationDialog: PropTypes.elementType,
    isPublic: PropTypes.bool,
    publicOrgId: PropTypes.string,
    publicFormId: PropTypes.string,
    allFields: PropTypes.array,
    formType: PropTypes.string,
    autoFillLookupFieldId: PropTypes.string,
    primaryFormId: PropTypes.string,
    isVerificationRequired: PropTypes.bool,
};
